// Renders a PSP's brand logo on a white pill so the original colours stay
// readable against PayHub's dark surfaces. Falls back to a 3-letter wordmark
// if a future PSP code lands before its artwork does.

import { cn } from '@/lib/utils';
import type { PspCode } from '@/api/types';
import { PSP_LOGOS } from './psp-schemas';

type Size = 'sm' | 'md' | 'lg';

const PILL: Record<Size, string> = {
  sm: 'h-7 w-10',
  md: 'h-8 w-12',
  lg: 'h-10 w-16',
};

const IMG: Record<Size, string> = {
  sm: 'max-h-5 max-w-9',
  md: 'max-h-6 max-w-10',
  lg: 'max-h-8 max-w-14',
};

export function PspLogo({
  psp,
  size = 'md',
  className,
}: {
  psp: PspCode;
  size?: Size;
  className?: string;
}) {
  const src = PSP_LOGOS[psp];
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded bg-white ring-1 ring-border/50 shrink-0',
        PILL[size],
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className={cn('object-contain', IMG[size])}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">
          {psp.slice(0, 3)}
        </span>
      )}
    </span>
  );
}

export function PspBrand({
  psp,
  size = 'md',
  className,
}: {
  psp: PspCode;
  size?: Size;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <PspLogo psp={psp} size={size} />
      <span className="capitalize font-medium">{psp}</span>
    </span>
  );
}
